/* eslint-disable no-nested-ternary */
/* eslint-disable no-unsafe-optional-chaining */
import ProfileImage from '@brands/Components/ProfileImage/ProfileImage';
import CareAdvisorProfileCardModal from '@brands/Dashboard/ProfileCards/CareAdvisor/ProfileCards';
import CollectorProfileCardModal from '@brands/Dashboard/ProfileCards/Collector/ProfileCards';
import PatientProfileCardModal from '@brands/Dashboard/ProfileCards/Patient/ProfileCards';
import { useVideoContext } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { getActiveVideoRoom } from '@brands/services/cases/getActiveVideoRoom';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { ICase } from '@brands/services/cases/types/ICase';
import { IVideoParticipant } from '@brands/services/communication/types/IVideo';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectVideoCallCredentials } from '@brands/store/selectors/videoCallCredentials';
import { displayErrorDetails } from '@brands/Utils/displayError';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography, useMediaQuery } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import ProviderProfileCardModal from '@brands/Dashboard/ProfileCards/Provider/ProfileCards';

type Props = {
  open: boolean;
  onClose: () => void;
  /** optional: if you want click on the mail icon to open chat etc */
  onMessageClick?: (userId: number) => void;
};

function buildParticipantsFromCase(caseData: ICase): IVideoParticipant[] {
  const entries = [
    caseData.patient,
    caseData.provider,
    caseData.care_advisor,
    caseData.collector ?? null,
  ];
  return entries
    .filter(Boolean)
    .map((profile) => ({
      identity: String(profile!.id),
      friendly_name: profile!.full_name,
      user: {
        id: profile!.id,
        full_name: profile!.full_name,
        picture_media_id: profile!.picture_media_id,
        role: profile!.role,
      },
    }));
}

const ParticipantsDialog: FC<Props> = ({ open, onClose, onMessageClick }) => {
  const { userInfo } = useAppSelector(selectAuth);
  const { vc_case_id: vcCaseID } = useAppSelector(selectVideoCallCredentials);
  const p = useParams();

  const { room } = useVideoContext();
  const fullScreen = useMediaQuery('(max-width:768px)');
  const [caseParticipants, setCaseParticipants] = useState<IVideoParticipant[]>();
  const [loadingParticipants, setLoadingParticipants] = useState(true);

  // Build the participant list:
  // - Always include myself (localParticipant.identity or userInfo.id as fallback)
  // - Include any remote participants from room.participants
  // - Match identities against caseParticipants (which holds full profile data)
  // - Deduplicate by user.id
  const list = useMemo(() => {
    if (!caseParticipants) return [];

    const onlineIds = new Set<string>();

    // Always add myself — localParticipant is excluded from room.participants by Twilio
    const myIdentity = room?.localParticipant?.identity;
    if (myIdentity) {
      onlineIds.add(myIdentity);
    } else if (userInfo?.id) {
      onlineIds.add(String(userInfo.id));
    }

    // Add remote participants
    if (room?.participants) {
      Array.from(room.participants.values()).forEach((rp) => onlineIds.add(rp.identity));
    }

    // Match against caseParticipants.identity (number) vs Twilio identity (string)
    const seen = new Set<number>();
    const result: IVideoParticipant[] = [];
    for (const cp of caseParticipants) {
      if (onlineIds.has(String(cp.identity)) && !seen.has(cp.user.id)) {
        seen.add(cp.user.id);
        result.push(cp);
      }
    }

    // Fallback: if nothing matched, show at least my caseParticipant entry
    if (result.length === 0 && userInfo?.id) {
      const me = caseParticipants.find((cp) => cp.user.id === userInfo.id);
      if (me) result.push(me);
    }

    return result;
  }, [caseParticipants, userInfo?.id, room]);
  // Optional: keep your existing “open profile card modal” behavior
  const [profileCardModal, setProfileCardModal] = useState(false);
  const [caProfileCardModal, setCAProfileCardModal] = useState(false);
  const [collectorProfileCardModal, setCollectorProfileCardModal] = useState(false);
  const [providerProfileCardModal, setProviderProfileCardModal] = useState(false);
  const [profileCardData, setProfileCardData] = useState<any>(null);
  const [caseData, setCaseData] = useState<any>(null);

  const caseIdNumber = Number(userInfo?.role?.name === UserRoleName.Patient ? p.caseId : vcCaseID);

  const handleParticipantClick = (participant: any) => {
    if (!caseData) return;
    if (participant.user.role.name === UserRoleName.Patient) {
      setProfileCardData(caseData.patient);
      setProfileCardModal(true);
    } else if (participant.user.role.name === UserRoleName.CareAdvisor) {
      setProfileCardData(caseData.care_advisor);
      setCAProfileCardModal(true);
    } else if (participant.user.role.name === UserRoleName.Provider) {
      setProfileCardData(caseData.provider);
      setProviderProfileCardModal(true);
    } else if (participant.user.role.name === UserRoleName.Collector) {
      setProfileCardData(caseData.collector);
      setCollectorProfileCardModal(true);
    }
  };

 useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoadingParticipants(true);
        const isPatient = userInfo.role.name === UserRoleName.Patient;
        if (isPatient) {
          const caseResponse = await getOneCase(caseIdNumber);
          if (!cancelled) {
            setCaseParticipants(buildParticipantsFromCase(caseResponse));
            setCaseData(caseResponse);
          }
        } else {
          const [activeVideoRoomResponse, caseResponse] = await Promise.all([
            getActiveVideoRoom(Number(vcCaseID), userInfo.role.name),
            getOneCase(caseIdNumber),
          ]);
          if (!cancelled) {
            setCaseParticipants(activeVideoRoomResponse.participants);
            setCaseData(caseResponse);
          }
        }
      } catch (error: unknown) {
        if (!cancelled) displayErrorDetails(error);
      } finally {
        if (!cancelled) setLoadingParticipants(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [open, vcCaseID, userInfo.role.name, caseIdNumber]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={fullScreen}
        sx={{ zIndex: 9300 }}
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 3,
            width: fullScreen ? '100%' : 520,
            maxWidth: fullScreen ? '100%' : 520,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Participants</Typography>
          <IconButton onClick={onClose} aria-label="Close participants dialog" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {loadingParticipants ? (
              <Typography sx={{ color: 'text.secondary', py: 2 }}>Loading...</Typography>
            ) : list.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', py: 2 }}>No participants</Typography>
            ) : (
              list.map((participant: any) => (
                <Box key={participant.user.id}>
                  <Box
                    onClick={() => handleParticipantClick(participant)}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1.25,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <ProfileImage id={participant.user.id.toString()} size="44px" isUpload={false} />

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.2 }}>
                            {participant.user.role.name}
                          </Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
                            {participant.user.full_name}
                          </Typography>
                        </Box>
                      </Box>

                      {/*                       <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMessageClick?.(participant.user.id);
                        }}
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 999,
                          bgcolor: '#F2F6FF',
                        }}
                        aria-label="Message participant"
                      >
                        <MailOutlineIcon fontSize="small" />
                      </IconButton> */}
                    </Box>
                  </Box>

                  <Divider />
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {profileCardModal && (
        <PatientProfileCardModal
          isOpen={profileCardModal}
          toggleModal={setProfileCardModal}
          patientIdentity={profileCardData}
        />
      )}
      {caProfileCardModal && (
        <CareAdvisorProfileCardModal
          isOpen={caProfileCardModal}
          toggleModal={setCAProfileCardModal}
          careAdvisorIdentity={profileCardData}
        />
      )}
      {collectorProfileCardModal && (
        <CollectorProfileCardModal
          isOpen={collectorProfileCardModal}
          toggleModal={setCollectorProfileCardModal}
          collectorIdentity={profileCardData}
        />
      )}
      {providerProfileCardModal && (
        <ProviderProfileCardModal
          isOpen={providerProfileCardModal}
          toggleModal={setProviderProfileCardModal}
          providerIdentity={profileCardData}
        />
      )}
    </>
  );
};

export default ParticipantsDialog;
